import { getSupabase, isSupabaseConfigured } from '../lib/supabase';

function mapRow(row) {
  let data = row.data;
  if (typeof data === 'string') {
    try {
      data = JSON.parse(data);
    } catch {
      data = null;
    }
  }
  return {
    id: row.id,
    type: row.type,
    title: row.title || '',
    body: row.body || '',
    read: !!row.is_read,
    at: row.created_at,
    data: data && typeof data === 'object' ? data : null,
  };
}

/** @param {string} appUserId public.users.id */
export async function fetchNotificationsForUser(appUserId, { limit = 80 } = {}) {
  const sb = getSupabase();
  if (!sb || !appUserId) return [];
  const { data, error } = await sb
    .from('push_notifications')
    .select('id, type, title, body, is_read, created_at, data')
    .eq('user_id', appUserId)
    .order('created_at', { ascending: false })
    .limit(limit);
  if (error) throw error;
  return (data || []).map(mapRow);
}

export async function countUnreadNotifications(appUserId) {
  const sb = getSupabase();
  if (!sb || !appUserId) return 0;
  const { count, error } = await sb
    .from('push_notifications')
    .select('id', { count: 'exact', head: true })
    .eq('user_id', appUserId)
    .eq('is_read', false);
  if (error) return 0;
  return count ?? 0;
}

export async function markNotificationReadRemote(notificationId, appUserId) {
  const sb = getSupabase();
  if (!sb || !appUserId || !notificationId) return;
  const { error } = await sb
    .from('push_notifications')
    .update({ is_read: true })
    .eq('id', notificationId)
    .eq('user_id', appUserId);
  if (error) throw error;
}

export async function markAllNotificationsReadRemote(appUserId) {
  const sb = getSupabase();
  if (!sb || !appUserId) return;
  const { error } = await sb
    .from('push_notifications')
    .update({ is_read: true })
    .eq('user_id', appUserId)
    .eq('is_read', false);
  if (error) throw error;
}

/** @param {string} notificationId @param {string} appUserId */
export async function deleteNotificationRemote(notificationId, appUserId) {
  const sb = getSupabase();
  if (!sb || !appUserId || !notificationId) return;
  const { error } = await sb.from('push_notifications').delete().eq('id', notificationId).eq('user_id', appUserId);
  if (error) throw error;
}

/**
 * Multiple components (header, dashboard header, hub page, etc.) can mount
 * useNotificationBadge() at the same time for the same logged-in user.
 * supabase-js dedupes `sb.channel(topic)` by topic string, so a second caller
 * gets back the SAME channel object the first caller already subscribed —
 * calling `.on()` on it then throws "cannot add postgres_changes callbacks
 * ... after subscribe()". We keep one real channel per appUserId here and
 * fan the count out to every listener instead.
 */
const unreadSubscriptions = new Map(); // appUserId -> { channel, listeners: Set<fn>, count: number }

/**
 * Subscribes to notification row changes for unread badge refresh.
 * @param {string} appUserId
 * @param {(n: number) => void} onUnreadCount
 * @returns {() => void} unsubscribe
 */
export function subscribeNotificationUnread(appUserId, onUnreadCount) {
  const sb = getSupabase();
  if (!sb || !appUserId) return () => {};

  let entry = unreadSubscriptions.get(appUserId);

  if (!entry) {
    entry = { channel: null, listeners: new Set(), count: 0 };
    unreadSubscriptions.set(appUserId, entry);

    const refresh = async () => {
      const n = await countUnreadNotifications(appUserId);
      entry.count = n;
      entry.listeners.forEach((fn) => fn(n));
    };

    entry.channel = sb
      .channel(`notifications-unread:${appUserId}`)
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'push_notifications',
          filter: `user_id=eq.${appUserId}`,
        },
        () => {
          void refresh();
        },
      )
      .subscribe();

    entry.refresh = refresh;
    void refresh();
  }

  entry.listeners.add(onUnreadCount);
  onUnreadCount(entry.count);

  return () => {
    const e = unreadSubscriptions.get(appUserId);
    if (!e) return;
    e.listeners.delete(onUnreadCount);
    if (e.listeners.size === 0) {
      unreadSubscriptions.delete(appUserId);
      void sb.removeChannel(e.channel);
    }
  };
}

export function canUseRemoteNotifications() {
  return isSupabaseConfigured && typeof getSupabase === 'function' && getSupabase() != null;
}
